import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Alert,
  CircularProgress,
  Toolbar,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Search,
  People,
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';

const API = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100').replace(/\/api\/v\d+\/?$/, '');

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('token');
  return token
    ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
}

type PersonnelRole = 'operator' | 'supervisor' | 'engineer' | 'manager';
type PersonnelStatus = 'active' | 'inactive';

export interface Personnel {
  id: string;
  firstName: string;
  lastName: string;
  employeeId: string;
  role: PersonnelRole;
  department: string;
  email?: string;
  phone?: string;
  status: PersonnelStatus;
  createdAt?: string;
}

const ROLES: PersonnelRole[] = ['operator', 'supervisor', 'engineer', 'manager'];

const defaultForm: Omit<Personnel, 'id'> = {
  firstName: '',
  lastName: '',
  employeeId: '',
  role: 'operator',
  department: '',
  email: '',
  phone: '',
  status: 'active',
};

const PersonnelTab: React.FC = () => {
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPerson, setEditingPerson] = useState<Personnel | null>(null);
  const [form, setForm] = useState<Omit<Personnel, 'id'>>(defaultForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const showError = (msg: string) => {
    setError(msg);
    setTimeout(() => setError(null), 5000);
  };

  const fetchPersonnel = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/v1/master-data/personnel`, { headers: authHeaders() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw { message: body?.message || `HTTP ${res.status}: Failed to fetch personnel` };
      }
      const data = await res.json();
      const list: Personnel[] = Array.isArray(data) ? data : data?.data ?? [];
      setPersonnel(list);
    } catch (err: any) {
      showError(err?.message || 'Failed to fetch personnel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPersonnel();
  }, [fetchPersonnel]);

  const filteredPersonnel = personnel.filter((p) => {
    if (!searchText) return true;
    const q = searchText.toLowerCase();
    return (
      `${p.firstName} ${p.lastName}`.toLowerCase().includes(q) ||
      p.employeeId.toLowerCase().includes(q) ||
      p.role.toLowerCase().includes(q) ||
      p.department.toLowerCase().includes(q)
    );
  });

  const openAddDialog = () => {
    setEditingPerson(null);
    setForm(defaultForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEditDialog = (person: Personnel) => {
    setEditingPerson(person);
    setForm({
      firstName: person.firstName,
      lastName: person.lastName,
      employeeId: person.employeeId,
      role: person.role,
      department: person.department,
      email: person.email ?? '',
      phone: person.phone ?? '',
      status: person.status,
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    if (!form.firstName.trim()) errors.firstName = 'First name is required';
    if (!form.lastName.trim()) errors.lastName = 'Last name is required';
    if (!form.employeeId.trim()) errors.employeeId = 'Employee ID is required';
    if (!form.department.trim()) errors.department = 'Department is required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (editingPerson) {
        const res = await fetch(`${API}/api/v1/master-data/personnel/${editingPerson.id}`, {
          method: 'PATCH',
          headers: authHeaders(),
          body: JSON.stringify(form),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw { message: body?.message || `HTTP ${res.status}: Failed to update personnel` };
        }
        const updated: Personnel = await res.json();
        setPersonnel((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      } else {
        const res = await fetch(`${API}/api/v1/master-data/personnel`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(form),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw { message: body?.message || `HTTP ${res.status}: Failed to create personnel` };
        }
        const created: Personnel = await res.json();
        setPersonnel((prev) => [created, ...prev]);
      }
      setDialogOpen(false);
    } catch (err: any) {
      showError(err?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (person: Personnel) => {
    const newStatus: PersonnelStatus = person.status === 'active' ? 'inactive' : 'active';
    try {
      const res = await fetch(`${API}/api/v1/master-data/personnel/${person.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw { message: body?.message || `HTTP ${res.status}: Failed to update status` };
      }
      const updated: Personnel = await res.json();
      setPersonnel((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
    } catch (err: any) {
      showError(err?.message || 'Failed to update status. Please try again.');
    }
  };

  const getRoleColor = (role: PersonnelRole): 'primary' | 'secondary' | 'info' | 'warning' => {
    switch (role) {
      case 'manager': return 'primary';
      case 'engineer': return 'info';
      case 'supervisor': return 'warning';
      default: return 'secondary';
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search personnel..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240 }}
          />
          <Box flex={1} />
          <Button variant="contained" startIcon={<Add />} onClick={openAddDialog}>
            Add Personnel
          </Button>
        </Toolbar>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Employee ID</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Department</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && filteredPersonnel.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Alert severity="info">
                      {searchText ? 'No personnel match your search.' : 'No personnel records found.'}
                    </Alert>
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                filteredPersonnel.map((person) => (
                  <TableRow key={person.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <People fontSize="small" color="action" />
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {person.firstName} {person.lastName}
                          </Typography>
                          {person.email && (
                            <Typography variant="caption" color="textSecondary">
                              {person.email}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {person.employeeId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={
                          person.role
                            ? person.role.charAt(0).toUpperCase() + person.role.slice(1)
                            : '—'
                        }
                        color={getRoleColor(person.role)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{person.department}</TableCell>
                    <TableCell>
                      <Chip
                        label={person.status === 'active' ? 'Active' : 'Inactive'}
                        color={person.status === 'active' ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEditDialog(person)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={person.status === 'active' ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          color={person.status === 'active' ? 'warning' : 'success'}
                          onClick={() => handleToggleStatus(person)}
                        >
                          {person.status === 'active' ? (
                            <ToggleOff fontSize="small" />
                          ) : (
                            <ToggleOn fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <People />
            {editingPerson
              ? `Edit: ${editingPerson.firstName} ${editingPerson.lastName}`
              : 'Add Personnel'}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <Box display="flex" gap={2}>
              <TextField
                label="First Name *"
                value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                error={!!formErrors.firstName}
                helperText={formErrors.firstName}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Last Name *"
                value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                error={!!formErrors.lastName}
                helperText={formErrors.lastName}
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>
            <Box display="flex" gap={2}>
              <TextField
                label="Employee ID *"
                value={form.employeeId}
                onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                error={!!formErrors.employeeId}
                helperText={formErrors.employeeId}
                size="small"
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Role *</InputLabel>
                <Select
                  value={form.role}
                  label="Role *"
                  onChange={(e) => setForm({ ...form, role: e.target.value as PersonnelRole })}
                >
                  {ROLES.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r.charAt(0).toUpperCase() + r.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Department *"
              value={form.department}
              onChange={(e) => setForm({ ...form, department: e.target.value })}
              error={!!formErrors.department}
              helperText={formErrors.department}
              size="small"
              fullWidth
            />
            <Box display="flex" gap={2}>
              <TextField
                label="Email"
                type="email"
                value={form.email ?? ''}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Phone"
                value={form.phone ?? ''}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                size="small"
                sx={{ flex: 1 }}
              />
            </Box>
            <FormControl size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={form.status}
                label="Status"
                onChange={(e) => setForm({ ...form, status: e.target.value as PersonnelStatus })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            {editingPerson ? 'Update' : 'Create'} Personnel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PersonnelTab;
